/**
 * Build a JSON-serializable brand context snapshot from a `BrandConfig`.
 *
 * Phase 5 of the brand-DNA unification — single shared helper consumed by
 * both ads and social dispatch paths so palette / voice / audience flow into
 * brief generation and managed-agent envelopes with one shape.
 *
 * The shape mirrors the BrandConfig groupings (`identity` / `visuals` /
 * `strategy`) but flattens to the fields downstream consumers actually use,
 * dropping empty values so server-side prompt builders can boolean-test
 * presence without a deep walk.
 *
 * Returns `null` when no usable brand identity is selected — callers should
 * omit `brandContext` from the wire body in that case.
 */
import type { BrandConfig, ColorEntry } from "@/config/brand";

export interface BrandContextIdentity {
  id?: string;
  name: string;
  description?: string;
  tagline?: string;
  genre?: string;
}

export interface BrandContextVisuals {
  palette: ColorEntry[];
  typography?: { headingFontId?: string; bodyFontId?: string };
}

export interface BrandContextStrategy {
  tone?: string;
  personality?: string;
  targetAudience?: string;
  uniqueValueProposition?: string;
  sellingPoints?: string[];
  guidelines?: string[];
  competitors?: string[];
  contentStrategy?: string;
  missionStatement?: string;
  market?: string;
}

export interface BrandContextSnapshot {
  identity: BrandContextIdentity;
  visuals: BrandContextVisuals;
  strategy: BrandContextStrategy;
  assets?: { logoUrl?: string };
}

const HEX_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

function trimToUndefined(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const t = value.trim();
  return t.length > 0 ? t : undefined;
}

function filterStrings(arr: unknown): string[] | undefined {
  if (!Array.isArray(arr)) return undefined;
  const out = arr.filter(
    (s): s is string => typeof s === "string" && s.trim().length > 0,
  );
  return out.length > 0 ? out : undefined;
}

export function buildBrandContextSnapshot(
  brand: BrandConfig | null | undefined,
): BrandContextSnapshot | null {
  if (!brand) return null;
  const name = trimToUndefined(brand.identity?.name);
  if (!name) return null;

  const strategy = brand.strategy ?? {};
  const visuals = brand.visuals ?? { colorPalette: [] };

  const palette: ColorEntry[] = (visuals.colorPalette ?? [])
    .map((c) => ({
      name: typeof c?.name === "string" ? c.name : "",
      hex: typeof c?.hex === "string" ? c.hex.trim() : "",
    }))
    .filter((c) => HEX_RE.test(c.hex));

  const typography =
    visuals.typography?.headingFontId || visuals.typography?.bodyFontId
      ? {
          ...(visuals.typography?.headingFontId
            ? { headingFontId: visuals.typography.headingFontId }
            : {}),
          ...(visuals.typography?.bodyFontId
            ? { bodyFontId: visuals.typography.bodyFontId }
            : {}),
        }
      : undefined;

  const identity: BrandContextIdentity = {
    name,
    ...(brand.identity?.id ? { id: brand.identity.id } : {}),
    ...(trimToUndefined(brand.identity?.description)
      ? { description: trimToUndefined(brand.identity?.description) }
      : {}),
    ...(trimToUndefined(strategy.brandTagline)
      ? { tagline: trimToUndefined(strategy.brandTagline) }
      : {}),
    ...(trimToUndefined(brand.identity?.genre)
      ? { genre: trimToUndefined(brand.identity?.genre) }
      : {}),
  };

  const tone = trimToUndefined(strategy.suggestedTone ?? strategy.toneOfVoice);
  const strategyOut: BrandContextStrategy = {
    ...(tone ? { tone } : {}),
    ...(trimToUndefined(strategy.brandPersonality)
      ? { personality: trimToUndefined(strategy.brandPersonality) }
      : {}),
    ...(trimToUndefined(strategy.targetAudience)
      ? { targetAudience: trimToUndefined(strategy.targetAudience) }
      : {}),
    ...(trimToUndefined(strategy.uniqueValueProposition)
      ? {
          uniqueValueProposition: trimToUndefined(
            strategy.uniqueValueProposition,
          ),
        }
      : {}),
    ...(filterStrings(strategy.sellingPoints)
      ? { sellingPoints: filterStrings(strategy.sellingPoints) }
      : {}),
    ...(filterStrings(strategy.guidelines)
      ? { guidelines: filterStrings(strategy.guidelines) }
      : {}),
    ...(filterStrings(strategy.competitors)
      ? { competitors: filterStrings(strategy.competitors) }
      : {}),
    ...(trimToUndefined(strategy.contentStrategy)
      ? { contentStrategy: trimToUndefined(strategy.contentStrategy) }
      : {}),
    ...(trimToUndefined(strategy.missionStatement)
      ? { missionStatement: trimToUndefined(strategy.missionStatement) }
      : {}),
    ...(trimToUndefined(strategy.market)
      ? { market: trimToUndefined(strategy.market) }
      : {}),
  };

  const logoAsset = brand.assets?.logo;
  const logoUrl = typeof logoAsset === "string" && logoAsset.length > 0 ? logoAsset : undefined;

  return {
    identity,
    visuals: { palette, ...(typography ? { typography } : {}) },
    strategy: strategyOut,
    ...(logoUrl ? { assets: { logoUrl } } : {}),
  };
}

/**
 * Adapter: convert a `BrandContextSnapshot` to the flat `BrandBrief` shape
 * the ads server expects (`brandBriefSchema` in
 * `core/src/domains/companion/services/brandContext.ts`). Used at the wire
 * boundary for ads dispatch + workspace-state context, so ads and social
 * derive their brand JSON from the same snapshot.
 */
export function brandSnapshotToBrief(
  snapshot: BrandContextSnapshot | null | undefined,
): Record<string, unknown> | undefined {
  if (!snapshot) return undefined;
  const { identity, visuals, strategy, assets } = snapshot;
  const colors = visuals.palette;
  const typography =
    visuals.typography?.headingFontId || visuals.typography?.bodyFontId
      ? {
          ...(visuals.typography?.headingFontId
            ? { headingFont: visuals.typography.headingFontId }
            : {}),
          ...(visuals.typography?.bodyFontId
            ? { bodyFont: visuals.typography.bodyFontId }
            : {}),
        }
      : undefined;
  return {
    id: identity.id ?? "",
    name: identity.name,
    ...(identity.tagline ? { tagline: identity.tagline } : {}),
    ...(identity.description ? { description: identity.description } : {}),
    ...(strategy.tone ? { tone: strategy.tone } : {}),
    ...(strategy.personality ? { personality: strategy.personality } : {}),
    ...(strategy.targetAudience
      ? { targetAudience: strategy.targetAudience }
      : {}),
    ...(strategy.uniqueValueProposition
      ? { uniqueValueProposition: strategy.uniqueValueProposition }
      : {}),
    ...(colors.length > 0 ? { colors } : {}),
    ...(typography ? { typography } : {}),
    ...(strategy.sellingPoints && strategy.sellingPoints.length > 0
      ? { sellingPoints: strategy.sellingPoints }
      : {}),
    ...(strategy.guidelines && strategy.guidelines.length > 0
      ? { guidelines: strategy.guidelines }
      : {}),
    ...(strategy.competitors && strategy.competitors.length > 0
      ? { competitors: strategy.competitors }
      : {}),
    ...(assets?.logoUrl ? { logoUrl: assets.logoUrl } : {}),
  };
}

const PALETTE_ROLES = ["Primary", "Secondary", "Accent", "Highlight", "Neutral"];

/**
 * Render the `# Brand DNA` markdown block injected into managed-agent task
 * messages on the ads dispatch path. Mirrors the server-side
 * `formatBrandDnaBlock` in
 * `core/src/domains/brand/services/brandContextSnapshot.ts` — the agents key
 * off the `# Brand DNA` heading to anchor palette + tone, so keep this
 * output byte-identical to the server renderer.
 *
 * Returns "" when no usable snapshot is supplied so callers can prepend the
 * block without a null branch.
 */
export function formatBrandDnaMarkdown(
  snapshot: BrandContextSnapshot | null | undefined,
): string {
  if (!snapshot) return "";
  const name = snapshot.identity.name?.trim();
  if (!name) return "";

  const visuals = snapshot.visuals;
  const strategy = snapshot.strategy;

  // Palette name fallback: the brand wizard saves unnamed swatches with empty
  // names; render those as Primary/Secondary/... so the LLM still sees a
  // meaningful label per swatch (snapshot keeps name="" so the wire JSON
  // remains lossless — fallback only applies at render time).
  const colors = (visuals.palette ?? []).map((c, i) => ({
    name: c.name?.trim() || PALETTE_ROLES[i] || `Color ${i + 1}`,
    hex: c.hex,
  }));
  const primaryHex = colors[0]?.hex;

  const tagline = snapshot.identity.tagline?.trim();
  const description = snapshot.identity.description?.trim();
  const tone = strategy.tone?.trim();
  const personality = strategy.personality?.trim();
  const targetAudience = strategy.targetAudience?.trim();
  const sellingPoints = (strategy.sellingPoints ?? []).filter(
    (s): s is string => typeof s === "string" && s.length > 0,
  );

  const lines = [
    `# Brand DNA — REQUIRED visual identity`,
    `Brand: ${name}`,
    tagline ? `Tagline: ${tagline}` : "",
    description ? `Brand description: ${description}` : "",
    tone ? `Tone of voice: ${tone}` : "",
    personality ? `Personality: ${personality}` : "",
    targetAudience ? `Target audience: ${targetAudience}` : "",
    colors.length
      ? `Brand color palette (MANDATORY — every variant must read as ${name}-branded):\n${colors
          .map((c) => `- ${c.name}: ${c.hex}`)
          .join("\n")}\nUse these exact hex values for backgrounds, callouts, accents, badges, frames, and any large color block. Pull headline / quote text colors from this palette (or from a high-contrast neutral against it). Do NOT introduce hues that are not in this list — no off-brand reds, blues, gradients, etc.${
          primaryHex ? ` Make ${primaryHex} the dominant brand color visible at first glance.` : ""
        }`
      : "",
    sellingPoints.length
      ? `Brand selling points:\n${sellingPoints.map((s) => `- ${s}`).join("\n")}`
      : "",
    `Honor this brand identity throughout the creative — palette, tone, and voice. The palette above is the single source of truth for color decisions.`,
  ].filter(Boolean);
  return lines.join("\n");
}
